/**
 * Checks if two values are deeply equal.
 * - Primitives: compared by value
 * - Objects: compared by properties (deep comparison)
 * - Arrays: compared by elements in order-independent manner
 *
 * @param value1 - First value to compare
 * @param value2 - Second value to compare
 * @returns true if values are deeply equal, false otherwise
 *
 * @example
 * isEqual(1, 1) // true
 * isEqual({a: 1}, {a: 1}) // true
 * isEqual([{a: 1}, {b: 2}], [{b: 2}, {a: 1}]) // true (order-independent for arrays)
 * isEqual([1, 2, 3], [3, 2, 1]) // true (order-independent)
 * isEqual({a: [1, 2]}, {a: [2, 1]}) // true (nested arrays are also order-independent)
 */
export function isEqual(value1: unknown, value2: unknown): boolean {
  if (value1 === value2) {
    return true;
  }

  if (value1 == null || value2 == null || typeof value1 !== 'object' || typeof value2 !== 'object') {
    return false;
  }

  const isArray1 = Array.isArray(value1);
  const isArray2 = Array.isArray(value2);

  if (isArray1 !== isArray2) return false;

  if (isArray1 && isArray2) {
    // TypeScript now knows both are arrays, but we need to assert for type narrowing
    const arr1 = value1 as unknown[];
    const arr2 = value2 as unknown[];

    if (arr1.length !== arr2.length) {
      return false;
    }

    const arr2Copy = [...arr2];

    return arr1.every((item1) => {
      const index = arr2Copy.findIndex((item2) => isEqual(item1, item2));
      if (index === -1) {
        return false;
      }
      arr2Copy.splice(index, 1);
      return true;
    });
  }

  const obj1 = value1 as Record<string, unknown>;
  const obj2 = value2 as Record<string, unknown>;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => keys2.includes(key) && isEqual(obj1[key], obj2[key]));
}
